/**
 * Dialog shown when closing tabs that have an active transaction, or one
 * still starting (`tabCloseGuardStore.requestTabsClose` — single close, Close
 * Others, Close to the Right, Close All). Offers Commit and Close, Rollback
 * and Close, or Cancel; tabs that are only starting a transaction have
 * nothing to commit, so they get Rollback (it abandons the BEGIN) and Cancel.
 */

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';

interface TransactionCloseDialogProps {
  open: boolean;
  onCommitAndClose: () => void;
  onRollbackAndClose: () => void;
  onCancel: () => void;
  isProcessing?: boolean;
  /** How many of the tabs being closed hold a transaction (default 1). */
  tabCount?: number;
  /** Every one of them is only starting its transaction. */
  startingOnly?: boolean;
}

function describe(tabCount: number, startingOnly: boolean): string {
  if (startingOnly) {
    return tabCount === 1
      ? 'This tab is starting a transaction. Close it anyway? The transaction is rolled back as soon as it starts.'
      : `${tabCount} of the tabs being closed are starting a transaction. Close them anyway? Each is rolled back as soon as it starts.`;
  }
  return tabCount === 1
    ? 'This tab has an active transaction. What would you like to do before closing?'
    : `${tabCount} of the tabs being closed have an active or starting transaction. What would you like to do before closing them?`;
}

export function TransactionCloseDialog({
  open,
  onCommitAndClose,
  onRollbackAndClose,
  onCancel,
  isProcessing = false,
  tabCount = 1,
  startingOnly = false,
}: TransactionCloseDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent data-testid="transaction-close-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{startingOnly ? 'Transaction Starting' : 'Active Transaction'}</AlertDialogTitle>
          <AlertDialogDescription>{describe(tabCount, startingOnly)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel onClick={onCancel} disabled={isProcessing}>
            Cancel
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={onRollbackAndClose}
            disabled={isProcessing}
            data-testid="transaction-rollback-and-close"
          >
            Rollback and Close
          </Button>
          {!startingOnly && (
            <Button
              onClick={onCommitAndClose}
              disabled={isProcessing}
              data-testid="transaction-commit-and-close"
            >
              Commit and Close
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
